import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import './TabMenu.css';
import {
    TEXT_TAB_MENU_DISPLAY,
    TEXT_TAB_MENU_MAP_VIEW,
    TEXT_TAB_MENU_SPLIT_VIEW,
    TEXT_TAB_MENU_LOCATION_ONE,
    TEXT_TAB_MENU_ADMIN_SETTINGS,
    DIALOG_MESSAGE_UPDATED_UDER_SUCCESSFULLY,
    DIALOG_ERROR_GENERAL_ERROR_MESSAGE,
    DIALOG_MESSAGE_ZOOM_IN_MAP
} from 'constants/message';
import { PAGE_LOCATION, PAGE_MAP, PAGE_USER_MANAGEMENT, PAGE_SPLIT } from 'utils/constanst';
import { useAuth } from 'context/AuthContext';
import { AccountType } from 'models';
import { useDialog } from 'context/DialogContext';
import Checkbox from 'components/Checkbox';
import { DEFAULT_ZOOM_RATIO, DUAL_CAMERA_TYPES, POPUP_ZOOM_RATIO } from 'constants/config';
import { apiUpdateDataUser } from 'apis/UserApi';
import FanLoadingIcon from 'components/FanLoadingIcon';
import { useSensors } from 'context/SensorsContext';
import { useSensorGroups } from 'context/SensorGroupsContext';
import { useGeneralSetting } from 'context/GeneralSettingContext';
import { useGeoCoordinate } from 'context/GeoCoordinateContext';

const MENU_ITEMS = [
    {
        id: PAGE_MAP,
        text: TEXT_TAB_MENU_MAP_VIEW,
    },
    {
        id: PAGE_SPLIT,
        text: TEXT_TAB_MENU_SPLIT_VIEW,
    },
    {
        id: PAGE_LOCATION,
        text: TEXT_TAB_MENU_LOCATION_ONE,
    },
];

const BASE_DISPLAY_ITEMS = [
    {
        label: 'カメラ方向',
        name: 'cameraDirection',
    },
    {
        label: '市境線',
        name: 'cityBoundary',
    },
    {
        label: '区境線',
        name: 'wardBoundary',
    },
];

const EMERGENCY_ROUTE_SITUATION_ITEM = {
    label: '緊急輸送路状況（未リリース）',
    name: 'emergencyRouteSituation',
};

const WARD_MAPPING = {
    // Yokohama Wards
    'Aoba Ward': '青葉区',
    'Asahi Ward': '旭区',
    'Hodogaya Ward': '保土ケ谷区',
    'Isogo Ward': '磯子区',
    'Kanagawa Ward': '神奈川区',
    'Kanazawa Ward': '金沢区',
    'Kohoku Ward': '港北区',
    'Konan Ward': '港南区',
    'Midori Ward': '緑区',
    'Minami Ward': '南区',
    'Naka Ward': '中区',
    'Nishi Ward': '西区',
    'Sakae Ward': '栄区',
    'Seya Ward': '瀬谷区',
    'Totsuka Ward': '戸塚区',
    'Tsurumi Ward': '鶴見区',
    'Tsuzuki Ward': '都筑区',
    'Izumi Ward': '泉区',
    // Niigate Wards
    'Minamiuonuma': '南魚沼区',
}

const TOOLTIP_TIMEOUT = 5000;

const propTypes = {
    selectedItem: PropTypes.number,
    onClickItem: PropTypes.func,
    popupZoomRatio: PropTypes.number,
    zoom: PropTypes.number,
};
const defaultProps = {
    popupZoomRatio: POPUP_ZOOM_RATIO,
    zoom: DEFAULT_ZOOM_RATIO,
};

const TabMenu = ({ selectedItem, onClickItem, popupZoomRatio, zoom }) => {
    const { currentUser, setCurrentUser } = useAuth();
    const [showPopup, setShowPopup] = useState(false);
    const [displayItems, setDisplayItems] = useState();
    const [loading, setLoading] = useState(false);
    const [tooltipVisible, setTooltipVisible] = useState({});
    const [openExpand, setOpenExpand] = useState({
        wardBoundary: false,
        trafficMonitoring: false,
    });
    const { error, success } = useDialog();
    const { sensors } = useSensors();
    const { sensorGroups } = useSensorGroups();
    const refCameraDirection = React.useRef({ dbValue: null, newValue: null });
    const refDisplayItemsOld = React.useRef();
    const { generalSetting } = useGeneralSetting();
    const { emergencyRoutes } = useGeoCoordinate();
    const disableAdminButton = currentUser?.accountType === AccountType.USER;
    const [district, ...wards] = generalSetting.borderSettings;

    // Build dynamic DISPLAY_ITEMS with emergency routes sorted by displayOrder
    const DISPLAY_ITEMS = React.useMemo(() => {
        const emergencyRouteItems = emergencyRoutes
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(route => ({
                label: route.routeName,
                name: `emergencyRoute_${route.id}`,
                color: route.color,
            }));

        return [
            ...BASE_DISPLAY_ITEMS,
            ...emergencyRouteItems,
            EMERGENCY_ROUTE_SITUATION_ITEM,
        ];
    }, [emergencyRoutes]);

    // Portal cleanup effect
    useEffect(() => {
        return () => {
            // Ensure any portal content is cleaned up on unmount
            if (showPopup) {
                setShowPopup(false);
            }
        };
    }, []);

    // Check if there is any directional camera
    const isDirectionalCamera = React.useMemo(() => {
        if (!sensors || !sensorGroups || sensors.length === 0 || sensorGroups.length === 0) return false;
        const cameraGroupId = sensorGroups?.find(group => group.type === 'CAMERA')?.id;
        if (!cameraGroupId) return false;

        const result = sensors.some((sensor) => {
            if (sensor.groupId !== cameraGroupId) return false;
            if (DUAL_CAMERA_TYPES.includes(sensor.data.typeCamera)) {
                return !!sensor?.data?.camera1Direction || !!sensor?.data?.camera2Direction;
            } else {
                return !!sensor?.data?.camera1Direction;
            }
        });
        refCameraDirection.current.newValue = result;
        return result;
    }, [sensors, sensorGroups]);


    useEffect(() => {
        const checkDirectionalCamera = async () => {
            const data = {
                id: currentUser.id,
                name: currentUser.name,
                email: currentUser.email,
                accountType: currentUser.accountType,
                contact: currentUser.contact,
                phone: currentUser.phone,
                enabledMFA: currentUser.enabledMFA,
                description: currentUser.description,
                data: {
                    ...currentUser.data,
                    displayItems: {
                        ...currentUser.data.displayItems,
                        cameraDirection: false
                    }
                },
            }
            const res = await apiUpdateDataUser(data);
            if (typeof res.data?.data === 'string') {
                res.data.data = JSON.parse(res.data.data);
            }
            setCurrentUser(res.data);
        };

        // if camera direction was enabled and now there is no directional camera, disable it
        if (refCameraDirection.current?.dbValue === true && refCameraDirection.current?.newValue === false)
            checkDirectionalCamera();
    }, [refCameraDirection.current?.newValue,]);

    // Retrieve display items from user data
    useEffect(() => {
        if (currentUser?.data) {
            const data = currentUser.data;
            if (data.displayItems) {
                setDisplayItems(data.displayItems);
                refCameraDirection.current.dbValue = data.displayItems?.cameraDirection;
                refDisplayItemsOld.current = JSON.stringify(data.displayItems);
                return;
            }
        } else {
            setDisplayItems({});
            refDisplayItemsOld.current = JSON.stringify({});
        }
    }, [currentUser?.data]);

    const onSaveDisplaySettings = async () => {
        setLoading(true);
        const data = {
            id: currentUser.id,
            name: currentUser.name,
            email: currentUser.email,
            accountType: currentUser.accountType,
            contact: currentUser.contact,
            phone: currentUser.phone,
            enabledMFA: currentUser.enabledMFA,
            description: currentUser.description,
            data: { ...currentUser.data, displayItems },
        }
        const res = await apiUpdateDataUser(data);
        if (res.success) {
            success(DIALOG_MESSAGE_UPDATED_UDER_SUCCESSFULLY);
            if (typeof res.data?.data === 'string') {
                res.data.data = JSON.parse(res.data.data);
            }
            setCurrentUser(res.data);
        } else {
            error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
        }
        setLoading(false);
        setShowPopup(false);
    }

    const onClear = () => {
        const clearedItems = DISPLAY_ITEMS.reduce((acc, item) => {
            acc[item.name] = false;
            return acc;
        }, {});

        setDisplayItems((prev) => ({
            ...prev,
            ...clearedItems,
        }));
    };

    const onChangeDisplayItem = (name) => {
        setDisplayItems((prev) => ({
            ...prev,
            [name]: !prev?.[name],
        }));

        // Show tooltip when changing from off to on camera direction
        if (zoom <= popupZoomRatio && name === DISPLAY_ITEMS[0].name) {
            if (!displayItems?.[name]) {
                setTooltipVisible(prev => ({
                    ...prev,
                    [name]: true
                }));

                // Hide tooltip after 5 seconds
                setTimeout(() => {
                    setTooltipVisible(prev => ({
                        ...prev,
                        [name]: false
                    }));
                }, TOOLTIP_TIMEOUT);
            }
        }
    };

    const onClosePopup = () => {
        setShowPopup(false);
        setOpenExpand({
            wardBoundary: false,
            trafficMonitoring: false,
        });
        if (refDisplayItemsOld.current)
            setDisplayItems(JSON.parse(refDisplayItemsOld.current));
    }

    // Create a stable portal container for all devices
    const portalContainer = React.useMemo(() => {
        if (typeof window !== 'undefined') {
            return document.body;
        }
        return null;
    }, []);

    // Render popup content
    const renderPopup = () => (
        <div className="popup-overlay">
            <div className="popup-content">
                <button className="popup-close" onClick={onClosePopup}>X</button>
                <div className="popup-body">
                    <div className="display-list">
                        <ul className="display-list_menu">
                            <li>
                                <div className="display-list_menu_item" style={{ position: 'relative' }}>
                                    <Checkbox disabled={!isDirectionalCamera} value={!!displayItems?.[DISPLAY_ITEMS[0].name]} onChange={() => onChangeDisplayItem(DISPLAY_ITEMS[0].name)} />
                                    <p>{DISPLAY_ITEMS[0].label}</p>
                                    {tooltipVisible[DISPLAY_ITEMS[0].name] && (
                                        <div className='tooltip'>
                                            {DIALOG_MESSAGE_ZOOM_IN_MAP}
                                        </div>
                                    )}
                                </div>
                            </li>
                            <li>
                                <div className="display-list_menu_item">
                                    <Checkbox value={!!displayItems?.[DISPLAY_ITEMS[1].name]} onChange={() => onChangeDisplayItem(DISPLAY_ITEMS[1].name)} />
                                    <p>{DISPLAY_ITEMS[1].label}</p>
                                    <hr style={{ backgroundColor: district.color }} />
                                </div>
                            </li>
                            <li>
                                <div className="display-list_menu_item">
                                    <Checkbox value={!!displayItems?.[DISPLAY_ITEMS[2].name]} onChange={() => onChangeDisplayItem(DISPLAY_ITEMS[2].name)} />
                                    <p>{DISPLAY_ITEMS[2].label}</p>
                                    <button
                                        className="collapse-toggle"
                                        onClick={() => setOpenExpand(prev => ({ ...prev, wardBoundary: !prev.wardBoundary }))}
                                        aria-label="Toggle ward boundary submenu"
                                    >
                                        {openExpand.wardBoundary ? '\u25bc' : '\u25b6'}
                                    </button>
                                </div>
                                {openExpand.wardBoundary && (
                                    <ul className="submenu">
                                        {wards.map((wardSetting) => {
                                            const name = wardSetting.location.displayName;
                                            const displayName = WARD_MAPPING[name.split(',')[0]];
                                            return (
                                                <li key={wardSetting.id} className="submenu-item">
                                                    <p>{displayName || ''}</p>
                                                    <hr style={{ backgroundColor: wardSetting.color }} />
                                                </li>
                                            )
                                        })}
                                    </ul>
                                )}
                            </li>
                            {DISPLAY_ITEMS.slice(3, -1).map((item, index) => (
                                <li key={item.name}>
                                    <div className="display-list_menu_item">
                                        <Checkbox value={!!displayItems?.[item.name]} onChange={() => onChangeDisplayItem(item.name)} />
                                        <p>{item.label}</p>
                                        {item.color && <hr style={{ borderTop: `3px dotted ${item.color}`, margin: 0 }} />}
                                    </div>
                                </li>
                            ))}
                            <li>
                                <div className="display-list_menu_item">
                                    <Checkbox value={!!displayItems?.[DISPLAY_ITEMS[DISPLAY_ITEMS.length - 1].name]} onChange={() => onChangeDisplayItem(DISPLAY_ITEMS[DISPLAY_ITEMS.length - 1].name)} />
                                    <p>{DISPLAY_ITEMS[DISPLAY_ITEMS.length - 1].label}</p>
                                     <button
                                        className="collapse-toggle"
                                        onClick={() => setOpenExpand(prev => ({ ...prev, trafficMonitoring: !prev.trafficMonitoring }))}
                                        aria-label="Toggle traffic monitoring submenu"
                                    >
                                        {openExpand.trafficMonitoring ? '\u25bc' : '\u25b6'}
                                    </button>
                                    </div>
                                    {openExpand.trafficMonitoring && (
                                        <ul className="submenu">
                                            <li key='cautionColor' className="submenu-item">
                                                <p>注意</p>
                                                <hr style={{ backgroundColor: generalSetting?.trafficMonitoring?.cautionColor }} />
                                            </li>
                                            <li key='warningColor' className="submenu-item">
                                                <p>警報</p>
                                                <hr style={{ backgroundColor: generalSetting?.trafficMonitoring?.warningColor }} />
                                            </li>
                                        </ul>
                                    )}
                            </li>
                        </ul>
                        <div className="display-list_buttons">
                            <button className="button_active" onClick={onSaveDisplaySettings}>
                                保存
                            </button>
                            <button className="button_cancel" onClick={onClear}>
                                クリア
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="tab_menu_horizontal_container">
            {selectedItem === PAGE_MAP && (
                <button
                    key={PAGE_LOCATION}
                    className={`tab_menu_tab_button`}
                    onClick={() => setShowPopup(true)}
                >
                    {TEXT_TAB_MENU_DISPLAY}
                </button>
            )}
            {MENU_ITEMS.map((item) => (
                <button
                    key={item.id}
                    className={`tab_menu_tab_button ${selectedItem === item.id ? 'active' : ''}`}
                    onClick={() => {
                        if (item.id === PAGE_MAP) {
                            const viewportMeta = document.querySelector('meta[name="viewport"]');
                            if (viewportMeta) {
                                viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
                                setTimeout(() => {
                                    viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes');
                                }, 50);
                            }
                        }
                        onClickItem(item.id)
                    }}
                >
                    {item.text}
                </button>
            ))}
            <button
                key={PAGE_USER_MANAGEMENT}
                className={`tab_menu_tab_button ${selectedItem === PAGE_USER_MANAGEMENT ? 'active' : ''} ${disableAdminButton ? 'disabled' : ''}`}
                onClick={!disableAdminButton ? () => onClickItem(PAGE_USER_MANAGEMENT) : undefined}
                disabled={disableAdminButton}
            >
                {TEXT_TAB_MENU_ADMIN_SETTINGS}
            </button>
            {showPopup && portalContainer &&
                ReactDOM.createPortal(renderPopup(), portalContainer)
            }
            {loading ? <FanLoadingIcon size={80} position='fixed' zIndex={9999} /> : null}
        </div>
    );
};
TabMenu.propTypes = propTypes;
TabMenu.defaultProps = defaultProps;
export default TabMenu;